import type {
  JsonCreate,
  JsonPaginatedResult,
  JsonPaginationOptions,
  JsonPayload,
  JsonRepositoryHelperContract,
  JsonTransactionContract,
  JsonUpdate,
  JsonUpdateOptions,
  PK,
  SK,
} from "@core/ports/json-storage";
import type { ZodObject } from "@core/zod";

export class DeviceRepositoryHelper<
  Identifier extends ZodObject,
  Data extends ZodObject,
  Transaction extends JsonTransactionContract,
  EntityType extends string,
> implements
    JsonRepositoryHelperContract<
      Identifier,
      Data,
      JsonTransactionContract,
      EntityType
    >
{
  public readonly __typeTag = "JsonRepositoryHelperContract";

  public readonly entityType: EntityType;

  private readonly helper: JsonRepositoryHelperContract<
    Identifier,
    Data,
    Transaction,
    EntityType
  >;

  private readonly transactionClass: new (
    ...args: readonly never[]
  ) => Transaction;

  public constructor({
    helper,
    transactionClass,
  }: {
    readonly helper: JsonRepositoryHelperContract<
      Identifier,
      Data,
      Transaction,
      EntityType
    >;
    readonly transactionClass: new (...args: readonly never[]) => Transaction;
  }) {
    this.entityType = helper.entityType;
    this.helper = helper;
    this.transactionClass = transactionClass;
  }

  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  public async create(
    create: JsonCreate<Identifier, Data, EntityType>,
  ): Promise<JsonPayload<Identifier, Data>> {
    return this.helper.create(create);
  }

  public async getById(
    PK: PK<EntityType>,
    SK: SK,
  ): Promise<JsonPayload<Identifier, Data> | null> {
    return this.helper.getById(PK, SK);
  }

  public async getByIds(
    keys: readonly { readonly PK: PK<EntityType>; readonly SK: SK }[],
  ): Promise<readonly JsonPayload<Identifier, Data>[]> {
    return this.helper.getByIds(keys);
  }

  public async getByPK(
    PK: PK<EntityType>,
    options?: JsonPaginationOptions,
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    return this.helper.getByPK(PK, options);
  }

  public async getBySKPrefix(
    PK: PK<EntityType>,
    SKPrefix: SK,
    options?: JsonPaginationOptions,
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    return this.helper.getBySKPrefix(PK, SKPrefix, options);
  }

  public async prepareCreate(
    transaction: JsonTransactionContract,
    create: JsonCreate<Identifier, Data, EntityType>,
  ): Promise<void> {
    await this.helper.prepareCreate(
      this.toBackendTransaction(transaction),
      create,
    );
  }

  public async prepareUpdateById(
    transaction: JsonTransactionContract,
    PK: PK<EntityType>,
    SK: SK,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<void> {
    await this.helper.prepareUpdateById(
      this.toBackendTransaction(transaction),
      PK,
      SK,
      update,
      options,
    );
  }

  /**
   * @throws {JsonWriteConflictError} If the stored etag differs from the
   *   one the options name.
   */
  public async updateById(
    PK: PK<EntityType>,
    SK: SK,
    update: JsonUpdate<Data>,
    options?: JsonUpdateOptions,
  ): Promise<JsonPayload<Identifier, Data> | null> {
    return this.helper.updateById(PK, SK, update, options);
  }

  private toBackendTransaction(
    transaction: JsonTransactionContract,
  ): Transaction {
    if (transaction instanceof this.transactionClass) {
      return transaction;
    }

    throw new Error(
      "The device helper takes a transaction its DeviceStorageManager started.",
    );
  }
}
